import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import SEO from '@/components/SEO';
import { useApp } from '@/context/AppContext';
import { Leaf, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { WeekInfo, isSameWeek, getCurrentISODayOfWeek } from '@/lib/dateUtils';

const VeckansLunch = () => {
  const { menus, currentWeekInfo } = useApp();
  const [selectedWeek, setSelectedWeek] = useState<WeekInfo>(currentWeekInfo);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (menus.length > 0 && !hasInitialized) {
      const isoDay = getCurrentISODayOfWeek();
      if (isoDay === 6) { // Lördag
        const nextWeekRaw = (currentWeekInfo.week % 52) + 1;
        const nextYearRaw = currentWeekInfo.week === 52 ? currentWeekInfo.year + 1 : currentWeekInfo.year;
        const nextWeekMenu = menus.find(m => m.week === nextWeekRaw && m.year === nextYearRaw);
        
        if (nextWeekMenu) {
          setSelectedWeek({ week: nextWeekRaw, year: nextYearRaw });
        }
      }
      setHasInitialized(true);
    }
  }, [menus, currentWeekInfo, hasInitialized]);

  const menu = menus.find(m => m.week === selectedWeek.week && m.year === selectedWeek.year);
  
  // Skapa en sorterad lista av tillgängliga veckor
  const availableWeeks: WeekInfo[] = menus
    .map(m => ({ week: m.week, year: m.year }))
    .sort((a, b) => a.year !== b.year ? a.year - b.year : a.week - b.week);

  const currentIndex = availableWeeks.findIndex(w => isSameWeek(w, selectedWeek));

  const goToPrevWeek = () => {
    if (currentIndex > 0) {
      setSelectedWeek(availableWeeks[currentIndex - 1]);
    }
  };

  const goToNextWeek = () => {
    if (currentIndex < availableWeeks.length - 1) {
      setSelectedWeek(availableWeeks[currentIndex + 1]);
    }
  };

  return (
    <Layout>
      <SEO 
        title={`Veckans Lunch v.${selectedWeek.week}`}
        description={`Se veckans lunchmeny för vecka ${selectedWeek.week} på Restaurang CFL i Söderhamn. Hemlagad mat, salladsbuffé och kaffe ingår.`}
      />
      {/* Header */}
      <section className="bg-secondary/30 py-12 md:py-16">
        <div className="container-wide mx-auto px-4 text-center">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            Veckans Lunch
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Hemlagad lunch varje dag. Alltid med vegetariskt alternativ.
          </p>
        </div>
      </section>

      {/* Week Selector */}
      <section className="py-8 border-b border-border sticky top-16 md:top-20 bg-background z-40">
        <div className="container-wide mx-auto px-4">
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevWeek}
              disabled={currentIndex <= 0}
            >
              <ChevronLeft size={20} />
            </Button>

            <div className="flex items-center gap-2 flex-wrap justify-center">
              {availableWeeks.map(weekInfo => (
                <button
                  key={`${weekInfo.year}-${weekInfo.week}`}
                  onClick={() => setSelectedWeek(weekInfo)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                    isSameWeek(selectedWeek, weekInfo)
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  )}
                >
                  V.{weekInfo.week}
                  {isSameWeek(weekInfo, currentWeekInfo) && (
                    <span className="ml-1 text-xs opacity-75">(nu)</span>
                  )}
                </button>
              ))}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={goToNextWeek}
              disabled={currentIndex >= availableWeeks.length - 1}
            >
              <ChevronRight size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Menu */}
      <section className="section-padding">
        <div className="container-narrow mx-auto px-4">
          {menu ? (
            <div className="space-y-4">
              {menu.items.map((item, index) => (
                  <div
                    key={item.id}
                    className="card-warm p-6 animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                        {item.day}
                      </div>
                      {item.comment && (
                        <div className="flex items-center gap-1 text-muted-foreground text-sm">
                          <MessageCircle size={14} />
                          <span>{item.comment}</span>
                        </div>
                      )}
                    </div>

                    {item.closed ? (
                      <div className="py-4 text-center">
                        <span className="inline-block px-6 py-2 bg-red-50 text-red-600 rounded-full font-medium text-lg">
                          {item.dish || 'Stängt'}
                        </span>
                      </div>
                    ) : (
                      <>
                        <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                          {item.dish}
                        </h3>
                        
                        {item.dish2 && (
                          <h3 className="font-display text-xl font-semibold text-foreground mb-4 pt-2 border-t border-border/40">
                            {item.dish2}
                          </h3>
                        )}

                        {item.vegetarian && (
                          <div className={cn("flex items-center gap-2 p-3 rounded-lg bg-vegetarian-light", item.dish2 && "mt-4")}>
                            <Leaf size={16} className="text-vegetarian shrink-0" />
                            <span className="text-vegetarian text-sm font-medium">Vegetariskt:</span>
                            <span className="text-foreground text-sm">{item.vegetarian}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg mb-2">
                Ingen meny tillgänglig för vecka {selectedWeek.week}, {selectedWeek.year}.
              </p>
              <p className="text-muted-foreground">
                Menyn är inte publicerad ännu. Kom tillbaka snart!
              </p>
            </div>
          )}

          {/* Info box */}
          <div className="mt-12 p-6 rounded-xl bg-secondary/50 text-center">
            <p className="text-foreground font-medium mb-2">
              Lunch serveras vardagar 10:30–13:30
            </p>
            <p className="text-muted-foreground text-sm">
              Priset inkluderar sallad, bröd, smör och kaffe/te.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default VeckansLunch;
